import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Select } from "antd";
import { debounce } from "lodash";
import PropTypes from "prop-types";

import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate }) => {
	const initialState = { jobs: [] };
	const [state, setState] = useState(initialState);
	const { jobs, submitting, visible } = state;
	const { id, job } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleSearch = debounce((search) => {
		if (search?.length >= 1) {
			API.job
				.list({ paginate: 12, search, status: "approved", subscribed: true })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						jobs: data.map(({ id, title }) => ({ value: id, label: title })),
					}));
				})
				.catch(() => {});
		}
	}, 500);

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				const action =
					hash === `#${Modals.CREATE}`
						? API.specialJob.create(values)
						: API.specialJob.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, submitting: false }));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		API.job
			.list({ paginate: 12, status: "approved", subscribed: true })
			.then(({ data }) => {
				setState((prevState) => ({
					...prevState,
					jobs: data.map(({ id, title }) => ({ value: id, label: title })),
				}));
			})
			.catch(() => {});

		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						jobs: [{ value: data.job?.id, label: data.job?.title }],
					}));

					form.setFieldsValue({ jobId: data.job?.id });
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={
				<>
					<Button type="primary" onClick={handleSubmit} loading={submitting}>
						{Resources.confirm}
					</Button>
					<Button onClick={() => navigate(-1)} loading={submitting}>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={
				hash === `#${Modals.CREATE}`
					? `${Resources.add}  ${Resources.jobs} ${Resources.special}`
					: `${Resources.edit} ${Resources.jobs} ${Resources.special} "${
							data?.job?.title || "-"
					  }"`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form layout="vertical" form={form}>
				<Form.Item name="jobId" label={Resources.job} rules={Rules.required}>
					<Select
						options={jobs}
						onSearch={handleSearch}
						filterOption={false}
						showArrow={false}
						showSearch
					/>
				</Form.Item>
				{hash === `#${Modals.CREATE}` && (
					<Form.Item
						name="days"
						label={`${Resources.count} ${Resources.day}`}
						rules={Rules.required}
					>
						<Input />
					</Form.Item>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
};

export default Component;
